import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import PublishIcon from '@material-ui/icons/Publish'

import { useOrganizationCompanyList } from '../../hooks/useOrganizationCompanyList'
import { useSignableDocCatalog } from '../../hooks/Outreach/useSignableDocs'
import { useUser } from '../../hooks/useUser'
import {
    useApplyOrgSignableDocToProperties,
    useOrgSignableDocs,
    useProvisionOrgSignableDoc,
} from './useOrgSignableDocApi'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: '100%',
    },
    headerRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
    },
    configSelect: {
        minWidth: 280,
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
    buttonRow: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    propertyList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
        maxHeight: 280,
        overflowY: 'auto',
    },
    meta: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
}))

export const EzFolioSignableDocsTab: React.FC = () => {
    const classes = useStyles()
    const { organization } = useUser()
    const organizationId = organization

    const apartmentsQuery = useOrganizationCompanyList(
        organizationId,
        'APARTMENT',
    )
    const apartments = apartmentsQuery.data ?? []
    const orgDocsQuery = useOrgSignableDocs(organizationId)
    const catalogQuery = useSignableDocCatalog({ pipeline_eligible: true })
    const provisionMutation = useProvisionOrgSignableDoc(organizationId)
    const applyMutation = useApplyOrgSignableDocToProperties(organizationId)

    const [selectedDocId, setSelectedDocId] = useState<number | ''>('')
    const [provisionOpen, setProvisionOpen] = useState(false)
    const [applyOpen, setApplyOpen] = useState(false)
    const [catalogTemplateKey, setCatalogTemplateKey] = useState('')
    const [newDocName, setNewDocName] = useState('')
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])

    const orgDocs = orgDocsQuery.data ?? []
    const catalogEntries =
        catalogQuery.data?.filter((entry) => entry.available) ?? []

    useEffect(() => {
        if (orgDocs.length > 0 && selectedDocId === '') {
            setSelectedDocId(orgDocs[0].id)
        }
    }, [orgDocs, selectedDocId])

    useEffect(() => {
        if (catalogEntries.length > 0 && !catalogTemplateKey) {
            setCatalogTemplateKey(catalogEntries[0].template_key)
            setNewDocName(catalogEntries[0].display_name)
        }
    }, [catalogEntries, catalogTemplateKey])

    const selectedDoc = useMemo(
        () => orgDocs.find((doc) => doc.id === selectedDocId) ?? null,
        [orgDocs, selectedDocId],
    )

    const toggleProperty = (propertyId: number) => {
        setSelectedPropertyIds((current) =>
            current.includes(propertyId)
                ? current.filter((id) => id !== propertyId)
                : [...current, propertyId],
        )
    }

    const handleProvision = async () => {
        if (!catalogTemplateKey) return
        await provisionMutation.mutateAsync({
            catalog_template_key: catalogTemplateKey,
            name: newDocName || undefined,
        })
        setProvisionOpen(false)
    }

    const handleApply = async () => {
        if (selectedDocId === '' || selectedPropertyIds.length === 0) return
        await applyMutation.mutateAsync({
            orgSignableDocId: selectedDocId,
            property_ids: selectedPropertyIds,
        })
        setApplyOpen(false)
    }

    if (orgDocsQuery.isLoading || catalogQuery.isLoading) {
        return (
            <Box className={classes.loading}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.headerRow}>
                <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.configSelect}
                >
                    <InputLabel id="org-signable-doc-label">
                        Org signable doc
                    </InputLabel>
                    <Select
                        labelId="org-signable-doc-label"
                        label="Org signable doc"
                        value={selectedDocId}
                        onChange={(event) =>
                            setSelectedDocId(Number(event.target.value))
                        }
                    >
                        {orgDocs.map((doc) => (
                            <MenuItem key={doc.id} value={doc.id}>
                                {doc.name} — {doc.catalog_template_key}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box className={classes.buttonRow}>
                    <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        color="primary"
                        onClick={() => setProvisionOpen(true)}
                        disabled={catalogEntries.length === 0}
                    >
                        Provision from catalog
                    </Button>
                    <Button
                        startIcon={<PublishIcon />}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedPropertyIds(
                                apartments.map((apt) => apt.id),
                            )
                            setApplyOpen(true)
                        }}
                        disabled={!selectedDoc || apartments.length === 0}
                    >
                        Apply to properties
                    </Button>
                </Box>
            </Box>

            {selectedDoc ? (
                <Typography className={classes.meta}>
                    {selectedDoc.doc_role.replace(/_/g, ' ')} ·{' '}
                    {selectedDoc.catalog_template_key}
                    {selectedDoc.created_from_template_version
                        ? ` · v${selectedDoc.created_from_template_version}`
                        : ''}
                </Typography>
            ) : (
                <Typography className={classes.meta}>
                    Provision an org signable doc from the EZOS catalog, then
                    apply deep copies to properties.
                </Typography>
            )}

            <Dialog
                open={provisionOpen}
                onClose={() => setProvisionOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Provision from catalog</DialogTitle>
                <DialogContent>
                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel>Catalog entry</InputLabel>
                        <Select
                            label="Catalog entry"
                            value={catalogTemplateKey}
                            onChange={(event) => {
                                const key = String(event.target.value)
                                setCatalogTemplateKey(key)
                                const entry = catalogEntries.find(
                                    (item) => item.template_key === key,
                                )
                                if (entry) {
                                    setNewDocName(entry.display_name)
                                }
                            }}
                        >
                            {catalogEntries.map((entry) => (
                                <MenuItem
                                    key={entry.template_key}
                                    value={entry.template_key}
                                >
                                    {entry.display_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={newDocName}
                        onChange={(event) => setNewDocName(event.target.value)}
                        style={{ marginTop: 16 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProvisionOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleProvision}
                        disabled={provisionMutation.isLoading}
                    >
                        Provision
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={applyOpen}
                onClose={() => setApplyOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Apply to properties</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        Copy {selectedDoc?.name} to selected properties.
                    </Typography>
                    <Box className={classes.propertyList}>
                        {apartments.map((apt) => (
                            <FormControlLabel
                                key={apt.id}
                                control={
                                    <Checkbox
                                        checked={selectedPropertyIds.includes(
                                            apt.id,
                                        )}
                                        onChange={() => toggleProperty(apt.id)}
                                        color="primary"
                                    />
                                }
                                label={apt.name}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApplyOpen(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleApply}
                        disabled={
                            applyMutation.isLoading ||
                            selectedPropertyIds.length === 0
                        }
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
